import { Subject, Observable, merge, of } from 'rxjs';
import { map, takeUntil, filter, scan, catchError } from 'rxjs/operators';

import { Inject, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { SnAbilityService } from '@lib/ability';
import { SnCamera, SnCameraGrid, SnContentType, SnLocation, SnShare, SnTag } from '@lib/api/entities';
import { SnCollection } from '@lib/common/models';
import { t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SN_DRAWER_DATA, SnDrawerService } from '@lib/ui/drawer';

import { ISnCanShare, ISnIsSharable } from '../../interfaces';
import { IShareConfigDrawerDataV2, ShareConfigDrawerV2Container } from '../share-config-drawer-v2/share-config-drawer-v2.container';
import {
  IShareInviteDrawerV2Data,
  ShareInviteDrawerV2Container,
} from '../share-invite-drawer-v2/share-invite-drawer-v2.container';

export interface IFsShareDrawerV2Data {
  object: ISnIsSharable;
  handlers: ISnCanShare;
}

@Component({
  selector: 'fs-share-drawer-v2',
  templateUrl: './share-drawer-v2.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./share-drawer-v2.container.scss'],
})
export class ShareDrawerV2Container implements OnDestroy {
  public readonly canAdd: boolean;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;

  public readonly shares$: Observable<SnCollection<SnShare>>;
  public readonly shareConfigAction: SnActionConfig;
  public readonly shareDeleteAction: SnActionConfig;

  private readonly _add$ = new Subject<SnShare[]>();
  private readonly _remove$ = new Subject<SnShare[]>();
  private readonly _update$ = new Subject<SnShare[]>();

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _drawer: SnDrawerService,
    @Inject(SN_DRAWER_DATA) public readonly data: IFsShareDrawerV2Data,
    ability: SnAbilityService,
  ) {
    this.canAdd = ability.can({ subject: 'sharing/share', action: 'add' });
    this.canChange = ability.can({ subject: 'sharing/share', action: 'change' });
    this.canDelete = ability.can({ subject: 'sharing/share', action: 'delete' });

    this.shareConfigAction = this._getShareConfigAction();
    this.shareDeleteAction = this._getShareDeleteAction();
    this.shares$ = this._connectShares();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get subheaderText(): string {
    switch (this.data.object.contentType) {
      case SnContentType.Camera: {
        const camera = this.data.object as SnCamera;
        return camera.location?.name
          ? `${camera.name} • ${camera.location.name}`
          : camera.name;
      }
      
      case SnContentType.Grid: {
        const grid = this.data.object as SnCameraGrid;
        return grid.name;
      }
      
      case SnContentType.Location: {
        const location = this.data.object as SnLocation;
        const address = location.address;
        return address ? `${location.name} • ${address}` : location.name;
      }
      
      case SnContentType.Tag: {
        const tag = this.data.object as SnTag;
        return tag.name;
      }
      
      default:
        return '';
    }
  }

  public addShare(): void {
    const drawer = this._drawer.open<ShareInviteDrawerV2Container, IShareInviteDrawerV2Data>(
      ShareInviteDrawerV2Container,
      {
        disableClose: true,
        data: {
            object: this.data.object,
            createHandler: this.data.handlers.createShare.bind(this.data.handlers),
          } as IShareInviteDrawerV2Data,
      }
    );

    drawer.afterClosed$.pipe(
      filter((shares) => !!shares),
      takeUntil(this._destroy$),
    ).subscribe((shares) => {
      this._add$.next(shares);
    });
  }

  public deleteShare(share: SnShare): void {
    this.data.handlers
      .deleteShare(this.data.object.id, share.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._remove$.next([share]);
      });
  }

  public configureShare(targetShare: SnShare): void {
    const drawer = this._drawer.open<ShareConfigDrawerV2Container, IShareConfigDrawerDataV2>(
      ShareConfigDrawerV2Container,
      {
        backdropClass: 'transparent',
        data: {
          share: targetShare,
          updateHandler: this.data.handlers.updateShare.bind(this.data.handlers),
        },
      },
    );

    drawer.afterClosed$
      .pipe(
        filter((share) => !!share),
        takeUntil(this._destroy$),
      )
      .subscribe((share) => {
        this._update$.next([share]);
      });
  }

  private _connectShares(): Observable<SnCollection<SnShare>> {
    const feed$ = this.data.handlers
      .getShareList(this.data.object.id, { pageSize: 'all' })
      .pipe(
        map((response) => response.results),
        catchError((error) => {
          console.error('Failed to load shares', error);
          return of<SnShare[]>([]);
        }),
      );

    const add$ = merge(this._add$, feed$).pipe(map((shares) => ({ shares, action: 'add' })));
    const update$ = this._update$.pipe(map((shares) => ({ shares, action: 'update' })));
    const delete$ = this._remove$.pipe(map((shares) => ({ shares, action: 'delete' })));

    return merge(add$, update$, delete$).pipe(
      scan((acc: SnCollection<SnShare>, { shares, action }) => {
        switch (action) {
          case 'add':
          case 'update':
            acc.set(...shares);
            break;
          case 'delete': 
            acc.delete(...shares);
            break;  
        }

        return acc;
      }, new SnCollection()),
    );
  }

  private _getShareConfigAction(): SnActionConfig<SnShare> {
    return {
      label: t('common.config'),
      icon: 'settings',
      hidden: !this.canChange,
      click: this.configureShare.bind(this),
    };
  }

  private _getShareDeleteAction(): SnActionConfig<SnShare> {
    return {
      label: t('shares.deleteShare'),
      icon: 'delete',
      hidden: !this.canDelete,
      click: this.deleteShare.bind(this),
    };
  }
}

